// 📁 src/lightversion/AIMentorLite.jsx

import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Stack,
  Divider,
  Alert,
} from "@mui/material";
import { motion } from "framer-motion";
import axios from "../utils/axiosInstance";
import { useNavigate } from "react-router-dom";
import { useLite } from "./context/LiteContext";
import LiteDayGate from "./LiteDayGate";
import LiteDayLayout from "./LiteDayLayout";
import ExperienceItem from "./components/ExperienceItem";
import LiteLearningBlock from "./components/LiteLearningBlock"; // ✅ NEW

/* ======================================================
   CONSTANTS
====================================================== */

const CHAT_STORAGE_KEY = "lite_ai_mentor_chat";

const REFLECTION_PROMPTS = [
  "What problem genuinely bothers you?",
  "Who is most affected by this problem?",
  "Why does this problem remain unresolved today?",
  "Why does this personally matter to you?",
  "Why would this still matter 5 years from now?",
];

const MAX_MESSAGES = REFLECTION_PROMPTS.length;

/* ======================================================
   COMPONENT
====================================================== */
const AIMentorLite = () => {
  const navigate = useNavigate();
  const { 
  setDay1Summary,
  markDayComplete,
  entryDecided,
  loading: liteLoading
  } = useLite();

  /* --------------------------------------------------
     🔐 ENTRY DECISION GUARD
  -------------------------------------------------- */
  useEffect(() => {

    if (liteLoading) return;

    if (!entryDecided && window.location.pathname !== "/lite/welcome") {
  navigate("/lite/welcome", { replace: true });
    }
    }, [entryDecided, liteLoading, navigate]);

  /* --------------------------------------------------
     Local State
  -------------------------------------------------- */
  const [message, setMessage] = useState("");
  const [chat, setChat] = useState([]);
  const [loading, setLoading] = useState(false);
  const [daySummary, setDaySummary] = useState(null);
  const [summarizing, setSummarizing] = useState(false);

  /* --------------------------------------------------
     Restore chat
  -------------------------------------------------- */
  useEffect(() => {
    const stored = localStorage.getItem(CHAT_STORAGE_KEY);
    if (stored) {
      try {
        setChat(JSON.parse(stored));
      } catch {
        localStorage.removeItem(CHAT_STORAGE_KEY);
      }
    }
  }, []);

  const saveChat = (next) => {
    setChat(next);
    localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(next));
  };

  /* --------------------------------------------------
     Bootstrap first mentor message
  -------------------------------------------------- */
  useEffect(() => {
    if (chat.length === 0) {
      saveChat([
        {
          role: "assistant",
          content:
            "Let’s begin gently.\n\nTake your time with this first question:\n\n" +
            REFLECTION_PROMPTS[0],
        },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* --------------------------------------------------
     Send reflection
  -------------------------------------------------- */
  const sendMessage = async () => {
    if (!message.trim()) return;

    const used = chat.filter((c) => c.role === "user").length;
    if (used >= MAX_MESSAGES) return;

    const next = [...chat, { role: "user", content: message }];
    saveChat(next);
    setMessage("");
    setLoading(true);

    try {
      const res = await axios.post("/lite/mentor", { message });
      saveChat([...next, { role: "assistant", content: res.data.reply }]);
    } catch {
      saveChat([
        ...next,
        {
          role: "assistant",
          content:
            "Pause if you need to. Thoughtful reflection matters more than speed.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  /* --------------------------------------------------
     🧠 AUTO-SUMMARY (DAY 1)
  -------------------------------------------------- */
  useEffect(() => {
    const reflections = chat.filter((c) => c.role === "user");
    if (reflections.length < MAX_MESSAGES || daySummary || summarizing) return;

    setSummarizing(true);

    axios
      .post("/lite/reflection/summarize", {
        reflections: reflections.map((c) => c.content).join("\n"),
        day: 1,
      })
      .then((res) =>
        setDaySummary(
          res.data?.summary ||
            "You are drawn to a meaningful problem that deserves deeper commitment."
        )
      )
      .finally(() => setSummarizing(false));
  }, [chat, daySummary, summarizing]);

  /* --------------------------------------------------
     FINALIZE DAY 1
  -------------------------------------------------- */
  const finalizeDayOne = () => {
    if (!daySummary) return;
    setDay1Summary(daySummary);
    markDayComplete();
    navigate("/lite/purpose");
  };

  const reflectionCount = chat.filter((c) => c.role === "user").length;

  /* ======================================================
     RENDER
  ===================================================== */
  return (
    <LiteDayGate allow={["MTP_DISCOVERY", "PERSONAL_REFLECTION"]}>
      <LiteDayLayout
        day={1}
        allowAction={!!daySummary}
        onComplete={finalizeDayOne}
      >
        <Box sx={{ maxWidth: 900, mx: "auto" }}>
          {/* HEADER */}
          <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }}>
            <Typography variant="h4" fontWeight="bold">
              AI Guide — Purpose Discovery
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1, mb: 4 }}>
              Day 1 · Reflection · No solutions yet
            </Typography>
          </motion.div>

          {/* EXPERIENCE CONTEXT */}
          <Stack spacing={4} sx={{ mb: 4 }}>
            <ExperienceItem
              icon="🎯"
              title="Focus on what truly bothers you"
              text="Don’t chase distractions—find the challenge that keeps you awake at night.We’re uncovering the problem that deserves your energy, passion, and years of commitment.This is where purpose meets persistence."
            />
            <ExperienceItem
              icon="🧠"
              title="Think slowly and honestly"
              text="Speed builds prototypes, but depth builds companies.Today is about digging into the real problem.Founders win by choosing clarity over haste."
            />
            <ExperienceItem
              icon="🌱"
              title="Build conviction before ideas"
              text="Every founder has ideas, but few have conviction.Clarity is your shield against wasted years.Start with belief, then build the business."
            />
          </Stack>

          {/* 🧠 INLINE LITE LEARNING (OPTIONAL) */}
          <LiteLearningBlock day={1} />

          {/* CHAT */}
          <Card sx={{ mb: 3, borderRadius: 3, mt: 4 }}>
            <CardContent>
              <Stack spacing={2}>
                {chat.map((msg, idx) => {
                  const isUser = msg.role === "user";
                  return (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: isUser ? 24 : -24 }}
                      animate={{ opacity: 1, x: 0 }}
                    >
                      <Box
                        sx={{
                          maxWidth: "85%",
                          p: 2.2,
                          borderRadius: 2,
                          bgcolor: isUser ? "primary.main" : "#f5f7fa",
                          color: isUser ? "#fff" : "#111",
                          ml: isUser ? "auto" : 0,
                        }}
                      >
                        <Typography
                          sx={{ whiteSpace: "pre-line", lineHeight: 1.65 }}
                        >
                          {msg.content}
                        </Typography>
                      </Box>
                    </motion.div>
                  );
                })}
              </Stack>
            </CardContent>
          </Card>

          {/* PROGRESS */}
          {!daySummary && (
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
              Reflection {reflectionCount} of {MAX_MESSAGES}
            </Typography>
          )}

          {/* INPUT */}
          {!daySummary && (
            <Stack spacing={2}>
              <TextField
                placeholder={REFLECTION_PROMPTS[reflectionCount]}
                multiline
                minRows={2}
                fullWidth
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                disabled={loading}
              />
              <Button
                variant="contained"
                size="large"
                onClick={sendMessage}
                disabled={loading}
              >
                Continue reflection
              </Button>
            </Stack>
          )}

          {/* SYNTHESIS */}
          {summarizing && (
            <Alert severity="info" sx={{ mt: 4 }}>
              Synthesizing your reflections…
            </Alert>
          )}

          {/* SUMMARY */}
          {daySummary && (
            <>
              <Divider sx={{ my: 4 }} />
              <Typography variant="h6" fontWeight="bold">
                Today’s conclusion
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                {daySummary}
              </Typography>
            </>
          )}
        </Box>
      </LiteDayLayout>
    </LiteDayGate>
  );
};

export default AIMentorLite;
